// src/components/pages/CustomerSearch.js
import React, { useState, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Pagination,
  Collapse,
  IconButton,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";

import { baseURL } from "../backend_url";

// ---------- helpers ----------

// compact UTC formatter
const formatUTC = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return (
      new Intl.DateTimeFormat("en-CA", {
        year: "2-digit",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: "UTC",
      }).format(d) + " UTC"
    );
  } catch {
    return String(iso);
  }
};

// flat mapping for export (controls field order + names)
const mapForExport = (r) => ({
  Ticket: r.ticketId || "",
  "Reservation #": r.reservationNo || "",
  "Call Type": r.callType || "",
  "Outbound Answered": r.outboundAnswered || "",
  Customer: r.customerName || "",
  Phone: r.phoneNumber || "",
  Email: r.contactEmail || "",
  Location: r.location || "",
  "Existing Customer": r.existingCustomer || "",
  "Source Detail": r.sourceDetail || "",
  "Call Category": r.callCategory || "",
  "Sale Status": r.saleStatus || "",
  "Sale Reason": r.saleReason || "",
  "Issue Resolved": r.issueResolved || "",
  "Not Resolved Reason": r.issueNotResolvedReason || "",
  "Call Transferred": r.callTransferred || "",
  "Transfer Reason": r.callTransferReason || "",
  "Pre Paid": r.prePaid || "",
  "Post Paid": r.postPaid || "",
  "Referred to Website": r.referredToWebsite || "",
  "Verification Done": r.verificationDone || "",
  "Created By": r.createdBy || "",
  "Created At (UTC)": formatUTC(r.createdAt) || "",
  "Ticket UTC": r.createdAtUTC || "",
  IP: r.ip || "",
  _id: r._id || "",
});

// trigger a browser download
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function CustomerSearch() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0 });
  const [dlFormat, setDlFormat] = useState("pdf"); // pdf | xlsx
  const [expandedId, setExpandedId] = useState(null);

  // --- small sort state for table ---
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    const copy = [...rows];

    copy.sort((a, b) => {
      let x = a[sortField];
      let y = b[sortField];

      // createdAt is date
      if (sortField === "createdAt") {
        x = x ? new Date(x).getTime() : 0;
        y = y ? new Date(y).getTime() : 0;
      }

      // numeric check
      if (!isNaN(x) && !isNaN(y)) {
        x = Number(x);
        y = Number(y);
      }

      if (x < y) return sortOrder === "asc" ? -1 : 1;
      if (x > y) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return copy;
  }, [rows, sortField, sortOrder]);

  // --- KPIs (this page only) ---
  const totalTicketsPage = sortedRows.length;
  const resolvedYesPage = useMemo(
    () => sortedRows.filter((r) => String(r.issueResolved).toLowerCase() === "yes").length,
    [sortedRows]
  );
  const resolvedNoPage = useMemo(
    () => sortedRows.filter((r) => String(r.issueResolved).toLowerCase() === "no").length,
    [sortedRows]
  );
  const transferredPage = useMemo(
    () => sortedRows.filter((r) => String(r.callTransferred).toLowerCase() === "yes").length,
    [sortedRows]
  );

  const totalPages = useMemo(
    () => (meta.total && meta.limit ? Math.ceil(meta.total / meta.limit) : 1),
    [meta.total, meta.limit]
  );

  const timestamp = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(
      d.getHours()
    )}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  };

  const search = async (override = {}) => {
    const query = (override.q ?? q).trim();
    const page = override.page ?? meta.page;
    const limit = override.limit ?? meta.limit;

    if (!query) {
      Swal.fire({
        title: "Missing search text",
        text: "Please enter Reservation #, Phone, Email, or Created By.",
        icon: "warning",
      });
      return;
    }

    try {
      setLoading(true);

      Swal.fire({
        title: "Processing your request…",
        text: "Please wait while we fetch customer interactions.",
        icon: "info",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await axios.get(`${baseURL}/customer_search`, {
        params: { q: query, page, limit },
        timeout: 15000,
      });

      setRows(res.data?.results || []);
      setMeta({
        page: res.data?.page || page,
        limit: res.data?.limit || limit,
        total: res.data?.total || 0,
      });
      setExpandedId(null);
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Search failed",
        text: err?.response?.data?.message || "Something went wrong. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
      Swal.close();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setMeta((m) => ({ ...m, page: 1 }));
    search({ page: 1 });
  };

  const handlePageChange = (event, value) => {
    if (value === meta.page) return;
    search({ page: value });
  };

  const handleDownload = async () => {
    if (!rows.length) {
      Swal.fire("No data", "No data to download. Run a search first.", "info");
      return;
    }
    const nameBase = `customer_search_${timestamp()}`;

    if (dlFormat === "xlsx") {
      try {
        const XLSXmod = await import("xlsx");
        const XLSX = XLSXmod.default || XLSXmod;
        const mapped = rows.map(mapForExport);
        const ws = XLSX.utils.json_to_sheet(mapped);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Results");
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        downloadBlob(blob, `${nameBase}.xlsx`);
      } catch (e) {
        console.error(e);
        Swal.fire(
          "Excel export needs the 'xlsx' package. Run: npm i xlsx"
        );
      }
      return;
    }

    if (dlFormat === "pdf") {
      try {
        const jsPDFmod = await import("jspdf");
        const autoTableMod = await import("jspdf-autotable");
        const jsPDF = jsPDFmod.default || jsPDFmod;
        // eslint-disable-next-line no-unused-vars
        const autoTable =
          (autoTableMod.default || autoTableMod).default || autoTableMod;

        const doc = new jsPDF({
          orientation: "portrait",
          unit: "pt",
          format: "a4",
        });
        const margin = { left: 36, right: 36 };
        const top = 40;

        rows.forEach((rec, idx) => {
          const mapped = mapForExport(rec);
          const entries = Object.entries(mapped);

          doc.setFont("helvetica", "bold");
          doc.setFontSize(12);
          const headerTitle =
            mapped["Ticket"] || mapped["Reservation #"] || `Record ${idx + 1}`;
          doc.text(
            `Customer Interaction — ${headerTitle}`,
            margin.left,
            top
          );

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.text(
            `Query: ${q || "-"}   •   Page ${meta.page}   •   Exported: ${formatUTC(
              new Date().toISOString()
            )}`,
            margin.left,
            top + 16
          );

          const body = entries.map(([k, v]) => [k, String(v ?? "")]);

          doc.autoTable({
            head: [["Field", "Value"]],
            body,
            startY: top + 28,
            theme: "grid",
            styles: { fontSize: 8, cellPadding: 3, overflow: "linebreak" },
            headStyles: {
              fillColor: [37, 99, 235],
              textColor: 255,
              fontStyle: "bold",
            },
            columnStyles: {
              0: { cellWidth: 170 },
              1: {
                cellWidth:
                  doc.internal.pageSize.getWidth() -
                  margin.left -
                  margin.right -
                  170,
              },
            },
            margin,
            didDrawPage: () => {
              doc.setFontSize(8);
              doc.text(
                `Page ${doc.internal.getNumberOfPages()}`,
                doc.internal.pageSize.getWidth() - 60,
                doc.internal.pageSize.getHeight() - 16
              );
            },
          });

          if (idx < rows.length - 1) {
            doc.addPage();
          }
        });

        doc.save(`${nameBase}.pdf`);
      } catch (e) {
        console.error(e);
        Swal.fire(
          "PDF export needs 'jspdf' and 'jspdf-autotable'. Run: npm i jspdf jspdf-autotable"
        );
      }
      return;
    }

    Swal.fire("Unknown format", "Choose PDF or Excel.", "warning");
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // ---------- UI ----------

  return (
    <Box p={4}>
      <Card
        elevation={3}
        sx={{
          maxWidth: 1200,
          mx: "auto",
          borderRadius: 3,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box mb={2}>
            <Typography variant="h5" fontWeight="bold">
              👤 Customer Search
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Find interactions by <b>Reservation #</b>, <b>Phone</b>, <b>Email</b>, or <b>Created By</b>.
            </Typography>
          </Box>

          {/* Search bar */}
          <Box component="form" onSubmit={onSubmit} mb={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Type reservation #, phone, email, or createdBy…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  startIcon={<SearchIcon />}
                  disabled={loading}
                  sx={{
                    py: 1,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {loading ? "Searching…" : "Search"}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Meta & Download */}
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Grid item>
              <Typography variant="body2" color="text.secondary">
                Results:&nbsp;
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight="bold"
                  color="text.primary"
                >
                  {meta.total || 0}
                </Typography>
              </Typography>
            </Grid>

            <Grid item>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  select
                  size="small"
                  label="Download as"
                  value={dlFormat}
                  onChange={(e) => setDlFormat(e.target.value)}
                  sx={{ minWidth: 180 }}
                  SelectProps={{ native: true }}
                >
                  <option value="pdf">PDF (full detail)</option>
                  <option value="xlsx">Excel (.xlsx)</option>
                </TextField>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  disabled={!rows.length}
                  sx={{ textTransform: "none" }}
                >
                  Download
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* KPI Cards (this page) – matching Vonage style */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: "#fff",
              mb: 2,
              pb: 1,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} md={2.4}>
                <Card sx={{ p: 2, textAlign: "center", bgcolor: "#e3f2fd" }}>
                  <Typography variant="body2">📊 Total Tickets</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {totalTicketsPage}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (this page)
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} md={2.4}>
                <Card sx={{ p: 2, textAlign: "center", bgcolor: "#e8f5e9" }}>
                  <Typography variant="body2">✅ Resolved = Yes</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {resolvedYesPage}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (this page)
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} md={2.4}>
                <Card sx={{ p: 2, textAlign: "center", bgcolor: "#ffebee" }}>
                  <Typography variant="body2">❌ Resolved = No</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {resolvedNoPage}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (this page)
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} md={2.4}>
                <Card sx={{ p: 2, textAlign: "center", bgcolor: "#fff3e0" }}>
                  <Typography variant="body2">🔁 Transferred</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {transferredPage}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (this page)
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card sx={{ p: 2, textAlign: "center", bgcolor: "#f1f5f9" }}>
                  <Typography variant="body2">📄 Total Pages</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {totalPages}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Current: {meta.page || 1}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Table + Expandable Details */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#e3f2fd",
                  }}
                >
                  <TableCell
                    align="center"
                    sx={{ backgroundColor: "#e3f2fd !important" }}
                  />
                  {[
                    "#",
                    "Ticket",
                    "Customer",
                    "Phone",
                    "Email",
                    "Issue Resolved",
                    "Created At (UTC)",
                    "Call Type",
                    "Source",
                  ].map((label) => {
                    const sortableFields = {
                      Ticket: "ticketId",
                      Customer: "customerName",
                      Phone: "phoneNumber",
                      "Created At (UTC)": "createdAt",
                    };

                    const field = sortableFields[label] || null;
                    const isActive = field && sortField === field;
                    const arrow =
                      isActive && (sortOrder === "asc" ? " ⬆" : " ⬇");

                    return (
                      <TableCell
                        key={label}
                        align="center"
                        sx={{
                          backgroundColor: "#e3f2fd !important",
                          fontWeight: "bold",
                          cursor: field ? "pointer" : "default",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => field && handleSort(field)}
                      >
                        {label}
                        {arrow || ""}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No results. Try another search.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((r, index) => {
                    const isOpen = expandedId === (r._id || index);
                    const resolved = String(r.issueResolved || "").toLowerCase();
                    const transfer = String(r.callTransferred || "").toLowerCase();

                    let resolvedColor = "default";
                    if (resolved === "yes") resolvedColor = "success";
                    else if (resolved === "no") resolvedColor = "error";

                    return (
                      <React.Fragment key={r._id || index}>
                        <TableRow
                          hover
                          sx={{
                            "&:hover": {
                              backgroundColor: "#f8fafc",
                            },
                          }}
                        >
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => toggleExpand(r._id || index)}
                            >
                              {isOpen ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </TableCell>

                          <TableCell align="center">
                            {(meta.page - 1) * meta.limit + index + 1}
                          </TableCell>
                          <TableCell align="center">
                            {r.ticketId || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {r.customerName || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {r.phoneNumber || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {r.contactEmail || "-"}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={r.issueResolved || "NA"}
                              color={resolvedColor}
                              variant={resolvedColor === "default" ? "outlined" : "filled"}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {formatUTC(r.createdAt)}
                          </TableCell>
                          <TableCell align="center">
                            {r.callType || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {r.sourceDetail || "-"}
                          </TableCell>
                        </TableRow>

                        {/* Expandable details row */}
                        {/* Expandable details row */}
                        <TableRow>
  <TableCell colSpan={10} sx={{ p: 0, background: "#f8fafc" }}>
    <Collapse in={isOpen} timeout="auto" unmountOnExit>
      <Box
        sx={{
          p: 2,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          my: 1,
          background: "#ffffff",
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 2, color: "#1e293b", fontSize: "15px" }}
        >
          Ticket Details
        </Typography>

        {/* 3-COLUMN GRID */}
        <Box
  sx={{
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    rowGap: 2,
    columnGap: 6,
  }}
>
  {[
    ["Reservation #", r.reservationNo],
    ["Location", r.location],
    ["Existing Customer", r.existingCustomer],
    ["Outbound Answered", r.outboundAnswered],
    ["Call Category", r.callCategory],
    ["Sale Status", r.saleStatus],
    ["Sale Reason", r.saleReason],
    ["Not Resolved Reason", r.issueNotResolvedReason],
    [
      "Call Transferred",
      [r.callTransferred, r.callTransferReason].filter(Boolean).join(" • ") || "-"
    ],
    [
      "Payment",
      `Pre: ${r.prePaid || "NA"} • Post: ${r.postPaid || "NA"}`
    ],
    [
      "Verification",
      `Verify: ${r.verificationDone || "NA"} • Web: ${r.referredToWebsite || "NA"}`
    ],
    ["Created By", r.createdBy],
    ["Ticket UTC", r.createdAtUTC],
    ["IP", r.ip],
  ].map(([label, value], idx) => (
    <Box
      key={idx}
      sx={{
        display: "grid",
        gridTemplateColumns: "150px 1fr",
        alignItems: "center",
      }}
    >
      {/* LABEL - LEFT ALIGNED */}
      <Typography
        sx={{
          textAlign: "left",
          color: "#475569",
          fontWeight: 600,
          fontSize: "13px",
          pr: 2,
          whiteSpace: "nowrap",
        }}
      >
        {label}:
      </Typography>

      {/* VALUE - CENTER ALIGNED */}
      <Typography
        sx={{
          textAlign: "center",
          color: "#0f172a",
          fontWeight: 500,
          fontSize: "14px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  ))}
</Box>

       

        {/* Notes Section */}
        {r.feedback && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#475569",
                mb: 0.8,
                textAlign: "left",
              }}
            >
              Feedback / Notes:
            </Typography>

            <Box
              sx={{
                p: 1.5,
                fontSize: "14px",
                borderRadius: 2,
                background: "#ffffff",
                border: "1px dashed #cbd5e1",
                textAlign: "left",
                whiteSpace: "pre-wrap",
              }}
            >
              {r.feedback}
            </Box>
          </Box>
        )}
      </Box>
    </Collapse>
  </TableCell>
</TableRow>

                       

            


                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Stack mt={2} alignItems="center">
            <Pagination
              count={totalPages}
              page={meta.page || 1}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

// Small helper for label/value in details
function DetailItem({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "-"}</Typography>
    </Grid>
  );
}
